/**
 * Solide 3D généré par le code (React Three Fiber). Chargé à la demande : les leçons sans 3D
 * ne téléchargent pas three.js.
 * Sobriété et petits téléphones : rendu seulement quand quelque chose change (frameloop="demand"),
 * résolution plafonnée, pas d'ombres, géométries simples. Sans WebGL : schéma et volume restent.
 * Pas de @react-three/drei : Metro (l'app) n'élague pas les imports et l'embarquerait en entier.
 */

/** Rotation au doigt (sans déplacement), rendu uniquement quand la caméra bouge, résolution réduite pendant le geste. */
export function Orbit({ target, minDistance, maxDistance, onEnd, maxPolar = Math.PI * 0.52 }: { target: [number, number, number]; minDistance: number; maxDistance: number; onEnd: () => void; maxPolar?: number }) {
  const { camera, gl, invalidate, setDpr } = useThree();
  const endRef = useRef(onEnd);
  endRef.current = onEnd;
  useEffect(() => {
    const c = new ThreeOrbit(camera, gl.domElement);
    c.enablePan = false;
    c.enableDamping = false;
    c.minDistance = minDistance;
    c.maxDistance = maxDistance;
    c.maxPolarAngle = maxPolar;
    c.target.set(...target);
    c.update();
    // Pendant la rotation on dessine en résolution 1 (fluidité), au repos on revient à la netteté maximale.
    const rest = Math.min(window.devicePixelRatio || 1, 1.5);
    const change = () => invalidate();
    const start = () => setDpr(1);
    const end = () => { setDpr(rest); invalidate(); endRef.current(); };
    c.addEventListener("change", change);
    c.addEventListener("start", start);
    c.addEventListener("end", end);
    return () => { c.removeEventListener("change", change); c.removeEventListener("start", start); c.removeEventListener("end", end); c.dispose(); };
  }, [camera, gl, invalidate, setDpr, minDistance, maxDistance, maxPolar, target[0], target[1], target[2]]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** Arêtes vives du solide (angle > 20°) : les génératrices du cône et du cylindre ne sont pas tracées. */
function Edges({ geometry }: { geometry: BufferGeometry | null }) {
  const edges = useMemo(() => (geometry ? new EdgesGeometry(geometry, 20) : null), [geometry]);
  useEffect(() => () => edges?.dispose(), [edges]);
  if (!edges) return null;
  return <lineSegments geometry={edges}><lineBasicMaterial color={C.ink} /></lineSegments>;
}

function DashedHeight({ h }: { h: number }) {
  const [line, setLine] = useState<ThreeLine | null>(null);
  useEffect(() => { line?.computeLineDistances(); }, [line, h]);
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <line ref={setLine as any}>
      <bufferGeometry key={h}><bufferAttribute attach="attributes-position" args={[new Float32Array([0, 0, 0, 0, h, 0]), 3]} /></bufferGeometry>
      <lineDashedMaterial color={C.amber} dashSize={0.25} gapSize={0.15} />
    </line>
  );
}
import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { BoxGeometry, ConeGeometry, CylinderGeometry, EdgesGeometry, SphereGeometry, type BufferGeometry, type Line as ThreeLine } from "three";
import { OrbitControls as ThreeOrbit } from "three/examples/jsm/controls/OrbitControls.js";
import { SOLID_SHAPES, solidDims, solidVolume, type SolidSpec } from "@jange/shared";
import { C, card, chip, fmt, hasWebGL, SliderRow } from "../theme.js";


const nf = (v: number) => (Math.round(v * 10) / 10).toString().replace(".", ",");

/** Hauteur et forme du solide, base posée sur le plan y = 0. */
export function SolidMesh({ spec, d }: { spec: SolidSpec; d: Record<string, number> }) {
  const { shape } = spec;
  const h = shape === "cube" ? d.c : shape === "sphere" ? 2 * d.r : d.h;
  // Géométrie construite tout de suite (et non par une ref) : les arêtes sont là dès la première image.
  const geo = useMemo<BufferGeometry>(() => {
    switch (shape) {
      case "cube": return new BoxGeometry(d.c, d.c, d.c);
      case "pave": return new BoxGeometry(d.L, d.h, d.l);
      // Cône à 4 côtés = pyramide à base carrée : rayon = demi-diagonale du carré.
      case "pyramide": return new ConeGeometry(d.c / Math.SQRT2, d.h, 4, 1);
      case "cone": return new ConeGeometry(d.r, d.h, 48, 1);
      case "cylindre": return new CylinderGeometry(d.r, d.r, d.h, 48, 1);
      case "sphere": return new SphereGeometry(d.r, 32, 16);
    }
  }, [shape, d.c, d.L, d.h, d.l, d.r]);
  useEffect(() => () => geo.dispose(), [geo]);
  const apex = shape === "pyramide" || shape === "cone";
  const see = apex && spec.show.height;
  return (
    <group>
      <group position={[0, h / 2, 0]} rotation={[0, shape === "pyramide" ? Math.PI / 4 : 0, 0]}>
        <mesh geometry={geo}>
          <meshStandardMaterial color={C.teal} transparent opacity={see ? 0.55 : 0.85} roughness={0.7} metalness={0} depthWrite={!see} />
        </mesh>
        {spec.show.edges && shape !== "sphere" && <Edges geometry={geo} />}
      </group>
      {spec.show.height && (apex || shape === "cylindre") && <DashedHeight h={h} />}
    </group>
  );
}

// Props constantes hors du rendu : un tableau recréé à chaque rendu relancerait un rendu 3D.
const LIGHT: [number, number, number] = [5, 8, 6];
const GL = { antialias: true, powerPreference: "low-power" as const };
const DPR: [number, number] = [1, 1.5];

/** Encombrement du solide aux valeurs maximales des curseurs : un cadrage fixe, qui ne saute pas quand un curseur bouge. */
export function solidExtent(spec: SolidSpec) {
  const max = solidDims(spec, Object.fromEntries(spec.sliders.map((s) => [s.name, s.max])));
  return Math.max(...Object.values(max).map((v) => (spec.shape === "sphere" || spec.shape === "cone" || spec.shape === "cylindre" ? v * 2 : v)), 1);
}

export type Solid3DProps = { spec: SolidSpec; params: Record<string, number>; lang: string; onParam: (name: string, v: number) => void; onInteract: () => void };

export default function Solid3D({ spec, params, lang, onParam, onInteract }: Solid3DProps) {
  const [webgl] = useState(hasWebGL);
  const d = solidDims(spec, params);
  const info = SOLID_SHAPES[spec.shape];
  // Cadrage fixe calculé sur les valeurs maximales des curseurs : la caméra ne saute pas quand l'élève bouge un curseur.
  const extent = useMemo(() => solidExtent(spec), [spec]);
  const volume = solidVolume(spec.shape, d);
  const orbitTarget = useMemo<[number, number, number]>(() => [0, extent / 3, 0], [extent]);
  const camera = useMemo(() => ({ position: [extent * 1.5, extent * 1.1, extent * 1.9] as [number, number, number], fov: 40, near: 0.1, far: extent * 20 }), [extent]);
  const grid = useMemo<[number, number, string, string]>(() => [extent * 2.2, 8, "#C9CFDA", "#E3E6EC"], [extent]);

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>{info.label}</div>
        <span style={{ display: "flex", gap: 6 }}>
          {spec.show.height && "h" in d && <span style={{ ...chip, color: C.amberText, background: C.amberTint }}>h = {nf(d.h)} {spec.unit}</span>}
          {webgl && <span style={chip}>Tourne-le du doigt</span>}
        </span>
      </div>
      {webgl ? (
        <div style={{ height: 280, borderRadius: 12, overflow: "hidden", background: "#F6F7F9", touchAction: "none" }}>
          <Canvas frameloop="demand" dpr={DPR} gl={GL} camera={camera}>
            <ambientLight intensity={0.75} />
            <directionalLight position={LIGHT} intensity={1.1} />
            <SolidMesh spec={spec} d={d} />
            <gridHelper args={grid} />
            <Orbit target={orbitTarget} minDistance={extent * 1.2} maxDistance={extent * 5} onEnd={onInteract} />
          </Canvas>
        </div>
      ) : (
        <div style={{ padding: 12, borderRadius: 12, background: C.amberTint, fontSize: 13, color: C.amberText }}>
          Ce téléphone n'affiche pas la 3D. Les curseurs et le calcul du volume fonctionnent quand même.
        </div>
      )}
      {spec.show.volume && (
        <div style={{ fontSize: 15, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "baseline" }}>
          <b>V</b> = {info.volume} <span style={{ color: C.muted }}>=</span> <b style={{ color: C.teal }}>{nf(volume)} {spec.unit}³</b>
          <span style={{ fontSize: 12, color: C.muted }}>({info.dims.map((k) => `${k} = ${fmt(d[k])}`).join(" · ")})</span>
        </div>
      )}
      {spec.sliders.map((s) => <SliderRow key={s.name} s={s} value={params[s.name] ?? s.initial} lang={lang} onChange={(v) => onParam(s.name, v)} />)}
    </div>
  );
}
